import { Helmet } from 'react-helmet-async'

interface BreadcrumbItem {
  name: string
  url: string
}

interface SeoProps {
  title: string
  description?: string
  /** Canonical path for this page, e.g. "/how-it-works". Absolute URLs are used as-is. */
  path?: string
  /** Absolute or site-relative image URL for social cards. */
  image?: string
  /** When true, tell crawlers not to index this page (e.g. 404, transactional pages). */
  noindex?: boolean
  breadcrumbs?: BreadcrumbItem[]
  structuredData?: object
  /** Skip the "| FixDelivered" suffix — for pages whose title is already long
   *  (e.g. plumber profiles: "{company} - Plumber in {city}"). */
  noBrandSuffix?: boolean
}

const SITE = 'https://fixdelivered.com'
const SITE_NAME = 'FixDelivered'
const DEFAULT_IMAGE = `${SITE}/apple-touch-icon.png`

export function Seo({ title, description, path, image, noindex, breadcrumbs, structuredData, noBrandSuffix }: SeoProps) {
  const fullTitle = noBrandSuffix || title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`
  const canonical = path ? (path.startsWith('http') ? path : `${SITE}${path}`) : undefined
  const ogImage = image ? (image.startsWith('http') ? image : `${SITE}${image}`) : DEFAULT_IMAGE

  const breadcrumbLd = breadcrumbs?.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((item, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: item.name,
          item: item.url.startsWith('http') ? item.url : `${SITE}${item.url}`,
        })),
      }
    : null

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {canonical && !noindex && <link rel="canonical" href={canonical} />}
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />
      {breadcrumbLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      )}
      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
    </Helmet>
  )
}
